import { store } from '~/actions/App/Http/Controllers/Api/AdEventController';

export interface DeliveryCard {
    delivery_token?: string;
    expires_at?: string;
    podcast_id?: number;
    itunes_id?: string;
    episode_id?: number | null;
    link?: string;
    outbound?: Record<string, string>;
}
interface SourceClick extends DeliveryCard {
    event_id: string;
    occurred_at: string;
}
interface CampaignEvent {
    token: string;
    type: string;
    event_id: string;
    occurred_at: string;
    [key: string]: unknown;
}
const historyKey = 'poddtoppen_ad_clicks_v2';
const clicksInMemory: SourceClick[] = [];

function clicks(): SourceClick[] {
    try {
        const stored: unknown = JSON.parse(localStorage.getItem(historyKey) ?? '[]');
        if (Array.isArray(stored))
            return stored.filter(
                (click: SourceClick) =>
                    click?.event_id && Date.parse(click.occurred_at) >= Date.now() - 86_400_000,
            );
    } catch {
        /* Storage can be unavailable. */
    }
    return clicksInMemory.filter(
        (click) => Date.parse(click.occurred_at) >= Date.now() - 86_400_000,
    );
}

function send(event: CampaignEvent, attempt = 0): void {
    try {
        const body = JSON.stringify(event);
        if (
            attempt === 0 &&
            ['click', 'impression'].includes(event.type) &&
            navigator.sendBeacon?.(store.url(), new Blob([body], { type: 'application/json' }))
        )
            return;
        void fetch(store.url(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            credentials: 'same-origin',
            keepalive: true,
        })
            .then((response) => {
                if (response.status === 409 || response.status >= 500) retry();
            })
            .catch(retry);
    } catch {
        /* Tracking must not prevent playback or navigation. */
    }
    function retry(): void {
        if (attempt < 5) setTimeout(() => send(event, attempt + 1), 2 ** attempt * 1000);
    }
}

export function trackDelivery(card: DeliveryCard, type: 'impression' | 'click'): void {
    if (!card.delivery_token || !card.expires_at || Date.parse(card.expires_at) <= Date.now())
        return;
    try {
        const existing =
            type === 'click'
                ? clicks().find((click) => click.delivery_token === card.delivery_token)
                : undefined;
        const event = {
            token: card.delivery_token,
            type,
            event_id: existing?.event_id ?? crypto.randomUUID(),
            occurred_at: existing?.occurred_at ?? new Date().toISOString(),
        };
        if (type === 'click' && !existing) {
            const source = { ...card, event_id: event.event_id, occurred_at: event.occurred_at };
            const history = [...clicks(), source];
            clicksInMemory.push(source);
            try {
                localStorage.setItem(historyKey, JSON.stringify(history));
            } catch {
                /* Optional storage. */
            }
        }
        send(event);
    } catch {
        /* Optional tracking. */
    }
}

export function campaignPlayback(podcastId: number, episodeId: number): () => void {
    const started = new Date().toISOString();
    const source = clicks()
        .filter(
            (click) =>
                Number(click.itunes_id) === podcastId &&
                (!click.episode_id || click.episode_id === episodeId),
        )
        .at(-1);
    let tracked = false;
    return () => {
        if (!source?.delivery_token || tracked) return;
        tracked = true;
        try {
            send({
                token: source.delivery_token,
                type: 'qualified_play',
                event_id: crypto.randomUUID(),
                occurred_at: new Date().toISOString(),
                source_event_id: source.event_id,
                episode_id: episodeId,
                playback_session_id: crypto.randomUUID(),
                session_started_at: started,
            });
        } catch {
            /* Optional tracking. */
        }
    };
}

export function trackCampaignPage(podcastId: number, episodeId?: number): void {
    const landing = clicks()
        .filter(
            (click) =>
                Number(click.itunes_id) === podcastId &&
                (click.episode_id ?? null) === (episodeId ?? null),
        )
        .at(-1);
    if (landing?.delivery_token) {
        send({
            token: landing.delivery_token,
            type: 'landing',
            event_id: crypto.randomUUID(),
            occurred_at: new Date().toISOString(),
            source_event_id: landing.event_id,
            target_path: location.pathname,
        });
    }
    const outbound = (event: MouseEvent): void => {
        const link =
            event.target instanceof Element
                ? event.target.closest<HTMLAnchorElement>('a[href]')
                : null;
        if (!link) return;
        const source = clicks()
            .filter(
                (click) =>
                    Number(click.itunes_id) === podcastId &&
                    (!click.episode_id || click.episode_id === episodeId),
            )
            .at(-1);
        if (!source?.delivery_token || !source.outbound?.apple) return;
        if (new URL(link.href).href !== new URL(source.outbound.apple, location.origin).href)
            return;
        send({
            token: source.delivery_token,
            type: 'outbound',
            event_id: crypto.randomUUID(),
            occurred_at: new Date().toISOString(),
            source_event_id: source.event_id,
            outbound_service: 'apple',
            target_url: link.href,
            podcast_id: source.podcast_id,
            episode_id: episodeId ?? null,
        });
    };
    document.addEventListener('click', outbound);
    document.addEventListener('auxclick', (event) => {
        if (event.button === 1) outbound(event);
    });
}
